import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import { api } from "../../app/Api/ApiSlice";
import Loader from "../../components/Loader";
import UserImage from "../../assets/user.png";
import { toast } from "react-toastify";

const PAGE_SIZE = 20;

const OrgPost = () => {
    const { organization } = useParams();
    const { state } = useLocation();
    const post = state?.post;

    // console.log(post)

    const [comments, setComments] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState("");
    const [newComment, setNewComment] = useState("");
    const [file, setFile] = useState(null);
    const [loadingSave, setLoadingSave] = useState(false);

    /* =======================
       MEDIA NORMALIZATION
    ======================= */
    const files = useMemo(() => {
        // Backward compatibility with old org posts
        if (post?.files) return post.files;

        if (post?.filesType && post?.filesUrls?.length) {
            return post.filesUrls.map(url => ({
                url,
                type: post.filesType === "images" ? "image" : post.filesType,
                name: "file"
            }));
        }

        return [];
    }, [post]);

    const imageFiles = files.filter(f => f.type === "image");

    const [focusedImg, setFocusedImg] = useState(
        imageFiles.length ? imageFiles[0].url : ""
    );


    /* =======================
       FETCH COMMENTS
    ======================= */
    const getComments = async (pageNumber = 1) => {
        try {
            setLoading(true);

            const res = await api.get(
                `/${post.organization.name}/posts/${post._id}/comments?page=${pageNumber}`
            );

            // console.log(res)
            const newComments = res.data || [];


            setComments(prev =>
                pageNumber === 1 ? newComments : [...prev, ...newComments]
            );

            setHasMore(newComments.length === PAGE_SIZE);
        } catch (err) {
            setErrorMsg("Error fetching comments");
        } finally {
            setLoading(false);
        }
    };

    /* =======================
       INIT
    ======================= */
    useEffect(() => {
        if (!post) return;

        setPage(1);
        getComments(1);
    }, [organization, post?._id]);

    if (!post) {
        return <p>Post data not available.</p>;
    }

    /* =======================
       MEDIA RENDERERS
    ======================= */
    const renderImages = () => (
        <>
            {focusedImg && (
                <div className="focused-image">
                    <img src={focusedImg} alt="post" />
                </div>
            )}

            {imageFiles.length > 1 && (
                <div className="image-selector">
                    {imageFiles.map((file, i) => (
                        <div
                            key={i}
                            className="image-selector-wrapper"
                            onClick={() => setFocusedImg(file.url)}
                        >
                            <img src={file.url} alt="" />
                        </div>
                    ))}
                </div>
            )}
        </>
    );

    const renderVideo = file => (
        <div className="video-container">
            <video controls>
                <source src={file.url} type="video/mp4" />
            </video>
        </div>
    );

    const renderFiles = () => {
        if (!files.length) return null;

        const first = files[0];

        switch (first.type) {
            case "image":
                return renderImages();
            case "video":
                return renderVideo(first);
            default:
                return null;
        }
    };

    const handleSubmit = async e => {
        e.preventDefault();

        if (!newComment && !file) {
            return toast.error("Comment or file required");
        }

        try {
            setLoadingSave(true);

            const formData = new FormData();
            formData.append("comment", newComment);
            if (file) formData.append("file", file);

            const res = await api.post(`${organization}/posts/${post._id}/comments`, formData);

            setComments(prev => [res.data, ...prev]);
            setNewComment("");
            setFile(null);

            toast.success("Comment added");
        } catch (err) {
            toast.error("Failed to add comment");
        } finally {
            setLoadingSave(false);
        }
    };


    /* =======================
       JSX
    ======================= */
    return (
        <div className="page-body">
            <div className="single-post-container">
                {/* POST */}
                <div className="single-post">
                    <div className="single-post-header">
                        <div className="post-user-info">
                            <img
                                src={post.organization?.logo?.url || UserImage}
                                alt=""
                                className="post-profile-image"
                            />
                            <div>
                                <p className="post-username">
                                    {post.organization?.name || organization}
                                </p>
                                <span className="post-timestamp">
                                    {new Date(post.createdAt).toLocaleDateString()}
                                </span>
                            </div>
                            <div className="">
                                <p className="post-audience">
                                    {post?.audience}
                                </p>
                                <p className="post-label">
                                    {post?.postLabel}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div>
                        <div className="orgpost-createdby">
                            <img
                                src={post.createdBy?.avatar?.url || UserImage}
                                alt=""
                                className="post-profile-image"
                            />
                            <p className="post-username">
                                {post.createdBy?.firstname} {post.createdBy?.lastname}
                            </p>
                        </div>
                        <span className="post-timestamp">
                            {new Date(post.createdAt).toLocaleDateString()}
                        </span>
                    </div>

                    <div className="single-post-content">
                        <p className="single-postpage-post-text">{post.postText}</p>
                        {renderFiles()}
                    </div>
                </div>

                {/* COMMENTS */}
                <div className="comments-section">
                    <h3>Comments</h3>

                    {loading && <Loader />}
                    {errorMsg && <p className="error">{errorMsg}</p>}
                    <form className="comment-form" onSubmit={handleSubmit}>
                        <textarea
                            placeholder="Write a comment..."
                            value={newComment}
                            onChange={e => setNewComment(e.target.value)}
                        />
                        <input type="file" onChange={e => setFile(e.target.files[0])} />
                        <button type="submit" disabled={loadingSave}>
                            {loadingSave ? "Saving..." : "Submit"}
                        </button>
                    </form>

                    <InfiniteScroll
                        dataLength={comments.length}
                        next={() => {
                            const nextPage = page + 1;
                            setPage(nextPage);
                            getComments(nextPage);
                        }}
                        hasMore={hasMore}
                        loader={<Loader />}
                        endMessage={
                            <p style={{ textAlign: "center" }}>No more comments</p>
                        }
                    >
                        <ul className="comments-list">
                            {comments.map(c => (
                                <li key={c._id} className="comment">
                                    <div className="comment-header">
                                        <img
                                            src={c.user?.profilePic?.url || UserImage}
                                            alt=""
                                            className="comment-profile-image"
                                        />
                                        <span>
                                            {c.user?.firstname} {c.user?.lastname}
                                        </span>
                                        <span>
                                            {new Date(c.createdAt).toLocaleDateString()}
                                        </span>
                                    </div>
                                    <p>{c.text || c.comment}</p>
                                </li>
                            ))}
                        </ul>
                    </InfiniteScroll>

                    {!comments.length && !loading && (
                        <p>No comments yet.</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OrgPost;
